<template>
  <div class="w-full h-full">
    <!-- Loader -->
    <div class="absolute bg-grey-100 bg-opacity-50 h-full left-0 top-0 w-full z-99" v-if="loading || loadingTemplates">
      <gd-loader :label="$t('frontend_loading')"/>
    </div>

    <div v-if="!loading" class="w-full h-full text-secondary-invert p-6 pt-2">
      <confirm-modal
        :ref="`confirmation-box-use-template`"
        :name="`confirmation-box-use-template`"
        :buttonText="$t('frontend_groovemail_frontend_ok')"
        :messageContent="$t('frontend_groovemail_template_fields_overriden')"
        :cancelText="$t('frontend_cancel')"
        :confirm-message='`
          ${$t("frontend_groovemail_template_fields_overriden_confirm")} "${templateToUse?.name}" ${$t("frontend_groovemail_template")}
        `'
        @confirm="useTemplate(true)"
      />
      <tabs
        :tabs="templateTabs"
        dark
      >
        <template :slot="$t('frontend_global_templates')">
          <div
            v-for="(template, index) in templates.global"
            :key="index"
            class="btn-radius relative mt-4"
          >
            <template-item
              :item="template"
              type="global"
              @useTemplate="openUseTemplateModal"
            ></template-item>
          </div>
        </template>
        <template :slot="$t('frontend_groovemail_my_templates_page_header')">
          <confirm-modal
            :ref="`confirmation-box-delete-template`"
            :name="`confirmation-box-delete-template`"
            :buttonText="$t('frontend_delete')"
            :cancelText="$t('frontend_cancel')"
            :confirm-message='`
              ${$t("frontend_groovemail_template_fields_delete_confirm")} "${templateToDelete?.name}" ${$t("frontend_groovemail_template")}
            `'
            @confirm="deleteTemplate(true)"
          />
          <gd-button class="w-full my-8" variant="primary" @click="saveAsTemplate">
            {{ $t('frontent_groovemail_save_current_form_as_new_template') }}
          </gd-button>
          <div
            v-for="(template, index) in templates.personal"
            :key="index"
            :class="{'mailform-sidebar-loop-group': templates.personal.length > 1}"
            class="mailform-sidebar-loop btn-radius relative mt-4"
          >
            <template-item
              :item="template"
              type="personal"
              @deleteTemplate="openDeleteTemplateModal"
              @useTemplate="openUseTemplateModal"
            ></template-item>
          </div>
        </template>
      </tabs>
    </div>
  </div>
</template>
<script>
import { mapState } from 'vuex';
import domtoimage from "dom-to-image-improved";
import Tabs from '@groovepages/gd-ui-kit/components/Tabs';
import themeConfig from '@/theme/themeConfig.js';
import showAlert from '@/components/sweet-alert-popup.js';

import TemplateItem from './TemplateItem.vue';
import Customfield from '@/models/Customfield';

export default {
  name: 'TemplatesMenu',
  components: {
    Tabs,
    TemplateItem,
  },
  props: {
      updateMenuOpen: {},
      activeTemplate: {},
      updateActiveTemplate: {},
  },
  data() {
    return {
      loaderColor: themeConfig.appPrimaryColor,
      loading: true,
      templateTabs: [this.$t('frontend_global_templates'), this.$t('frontend_groovemail_my_templates_page_header')],
      activeTab: 2,
      templateToUse: null,
      templateToDelete: null,
    };
  },
  computed: {
    ...mapState({
      templates: state => state.forms.templates,
      loadingTemplates: state => state.forms.loadingTemplates,
    }),
    currentTemplate: {
      get() {
        return this.activeTemplate;
      },
      set(template) {
        this.updateActiveTemplate(template);
      },
    },
  },

  async mounted() {
    try {
      await this.$store.dispatch('forms/loadTemplates');
      this.hideLoader();
    } catch (error) {
      this.requestErrorAlert(error.response?.data?.error);
    }
  },

  methods: {
    // error message for the request field
    async requestErrorAlert(error) {
      // hide the loader
      this.loading = false;
      
      await showAlert({
        iconType: 'alert-error',
        html : `
          <style>.swal2-cancel { color: ${themeConfig.appPrimaryColorInvert} !important; }</style>
          <p class="text-sm text-deep-dark mb-3">${error || this.$t('frontend_backend_error') }</p>
        `,
        showCancelButton: false,
        showCloseButton: true,
        confirmButtonColor: themeConfig.appPrimaryColor,
        confirmButtonText: this.$t('frontend_close'),
      });
    },
  
    async openDeleteTemplateModal (item) {
      this.templateToDelete = item;
      this.$refs[`confirmation-box-delete-template`].open();
    },
    async openUseTemplateModal (item) {
      this.templateToUse = item;
      this.$refs[`confirmation-box-use-template`].open();
    },

    // delete template
    async deleteTemplate(confirmed) {

      // if confirmed delete template
      if (confirmed) {

        // delete template from db and store
        this.$store.dispatch('forms/deleteTemplate', this.templateToDelete);
      }
      this.templateToDelete = null;
    },

    // use template
    async useTemplate(confirmed) {
      if (confirmed) {
        try {
          await this.$store.dispatch('forms/useTemplate', {
            template: this.templateToUse, form_id: this.$route.params.id
          });

          // Handle Custom Fields

          let accountCustomFields = [];
          let fieldsToHandle = [];
          const templateCustomFields = JSON.parse(this.templateToUse.form_fields).fields.filter(i => i.fieldtype === 'custom');

          if (templateCustomFields.length > 0) {
            accountCustomFields = await Customfield.$get();
            fieldsToHandle = templateCustomFields.filter(
              templateField => !accountCustomFields.some(
                accountField => accountField.id === templateField.id || accountField.origin === templateField.id
              )
            );
          }

          if (fieldsToHandle.length > 0) {

            // Save
            fieldsToHandle.forEach(async (i) => {
              const customfield = new Customfield({});
              customfield.identifier = i.identifier;
              customfield.type = i.type;
              customfield.fieldplaceholder = i.placeholder;
              customfield.fieldlabel = i.label;
              customfield.fieldoptions = JSON.stringify(i.options);
              customfield.mandatory = i.mandatory;
              customfield.origin = i.id;
              customfield.icon = i.icon;
              customfield.default = i.default;
              await customfield.save();
            });

            // TODO check for same identifier
            // TODO check origin for multiple origins (maybe)

            this.$root.$emit('updateCustomFields');
          }
          this.$root.$emit('updateFormFields', JSON.parse(this.templateToUse.form_fields));
          this.templateToUse = null;

        } catch (error) {
          this.requestErrorAlert(error.response?.data?.error);
        }
      }
    },
    async takeThumbnail(){
      this.$store.commit('forms/SET_PRINTING', true);

      this.$nextTick(() => {
        const phoneInput = document.querySelector('#telephone_inputundefined');
        if (phoneInput) {
          const phoneParent = phoneInput.parentElement;
          const phoneInputHeight = phoneParent.querySelector('input').offsetHeight;
          phoneParent.querySelector('div').style.height = `${phoneInputHeight}px`;
        }
      });

      const node = document.getElementById('formhtml');

      try {
        const base64Image = await domtoimage.toPng(node);
        this.$store.commit('forms/SET_PRINTING', false);

        return base64Image;
      } catch (error) {
        this.requestErrorAlert(error.response?.data?.error);

        return null;
      }
    },
    async saveAsTemplate() {
      try {
        const thumbnailData = await this.takeThumbnail();
        await this.$store.dispatch('forms/saveAsTemplate', {
          form_id: this.$route.params.id,
          thumbnail_data: thumbnailData
        });

      } catch (error) {
        this.requestErrorAlert(error.response?.data?.error);
      }
    },
    showLoader() {
      this.loading = true;
    },
    hideLoader() {
      setTimeout(() => {
        this.loading = false;
      }, 1500);
    },
  },
};
</script>
